import type { SunMoment } from '../../shared/palette';
import { nextFloat, seedRng } from '../../shared/prng';
import type { Tempo } from '../../shared/tempo';
import type { Vec2 } from '../../shared/vec';
import { easeOutQuad } from '../motion';

export const PUFF_COUNT = 16;
const RISE_BEATS = 2;
const RISE_LIFT = 26;
const PEAK_AT = 0.15;
const SCALE = { from: 0.6, to: 1.4 };
const RADIUS = { min: 10, spread: 12 };
const HAZE_RISE = 0.4;
export const FLOOR = { inner: 110, outer: 410, stretchX: 1.2, stretchY: 0.8 };

export interface Puff {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
}

export interface DustLook {
  readonly tone: string;
  readonly alpha: number;
}

export const DUST_LOOKS: Record<SunMoment, DustLook> = {
  crepuscule: { tone: '#ffc088', alpha: 0.4 },
  nuit: { tone: '#ffcf8a', alpha: 0.35 },
  aube: { tone: '#d9a050', alpha: 0.5 },
  jour: { tone: '#c99a40', alpha: 0.55 },
};

function sinceKick(index: number, now: number, tempo: Tempo): number {
  return now - (index % RISE_BEATS) * tempo.ticksPerBeat;
}

export function launchOf(index: number, now: number, tempo: Tempo, calm: boolean): number {
  return calm ? 0 : Math.floor(sinceKick(index, now, tempo) / (RISE_BEATS * tempo.ticksPerBeat));
}

export function riseOf(index: number, now: number, tempo: Tempo, calm: boolean): number {
  if (calm) {
    return HAZE_RISE;
  }
  const period = RISE_BEATS * tempo.ticksPerBeat;
  const since = sinceKick(index, now, tempo) % period;
  return (since < 0 ? since + period : since) / period;
}

export function puffOf(seed: number, index: number, launch: number, center: Vec2): Puff {
  const rng = seedRng((seed ^ Math.imul(launch * PUFF_COUNT + index + 1, 0x9e3779b1)) >>> 0);
  const reach = FLOOR.inner + nextFloat(rng) * (FLOOR.outer - FLOOR.inner);
  const angle = nextFloat(rng) * Math.PI * 2;
  return {
    x: center.x + reach * Math.cos(angle) * FLOOR.stretchX,
    y: center.y + reach * Math.sin(angle) * FLOOR.stretchY,
    radius: RADIUS.min + nextFloat(rng) * RADIUS.spread,
  };
}

export function puffLift(rise: number): number {
  return RISE_LIFT * easeOutQuad(rise);
}

export function puffScale(rise: number): number {
  return SCALE.from + (SCALE.to - SCALE.from) * easeOutQuad(rise);
}

export function puffOpacity(rise: number): number {
  return rise < PEAK_AT ? rise / PEAK_AT : (1 - rise) / (1 - PEAK_AT);
}
